import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { BadgeAward } from '../components/badges/BadgeFanfare';
import { api, ApiError } from '../lib/api';
import { useAsync, useSession, useToast } from '../lib/store';
import type {
  BondEvent,
  EvolutionOption,
  InventoryEntry,
  LineageSummary,
  Pokemon,
  Ribbon as RibbonData,
  RibbonCategory,
} from '../lib/types';
import { LineagePanel } from '../components/lineage/FamilyTree';
import { Bar, Button, Confetti, ErrorState, Modal, Panel, ShinyChip, Tabs, TypeChip } from '../components/ui';
import { ExpBar } from '../components/game';
import { BondMeter } from '../components/bond/BondMeter';
import { RibbonSash } from '../components/ribbons/Ribbon';
import { dexNumber, formatDate, formatNumber, genderSuffix, STAT_LABELS, timeAgo } from '../lib/format';

interface DetailResponse {
  pokemon: Pokemon;
  isOwner: boolean;
  owner: { id: string; username: string };
  originalOwner: string | null;
  evolutions: EvolutionOption[];
  recentFeeders: Array<{ username: string; expGranted: number; createdAt: string }>;
  lineage: LineageSummary;
  /** Where a World Boss Legendary came from — PART 12. Null for everything else. */
  worldBossOrigin: {
    eventKey: string;
    eventName: string;
    bossTitle: string;
    bossName: string;
    bossSpeciesId: number;
    rank: number;
    originalWinnerName: string;
    damage: number;
    participants: number;
    defeatedAt: string;
  } | null;
}

export function PokemonDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { refresh, celebrate, celebrateBadges } = useSession();
  const { push } = useToast();

  const state = useAsync<DetailResponse>(() => api.get<DetailResponse>(`/pokemon/${id}`), [id]);
  const inventory = useAsync<{ items: InventoryEntry[] }>(() => api.get('/me/inventory'));

  const [renaming, setRenaming] = useState(false);
  const [nickname, setNickname] = useState('');
  const [busy, setBusy] = useState(false);
  const [evolvedTo, setEvolvedTo] = useState<{ fromName: string; fromSprite: string; pokemon: Pokemon } | null>(null);
  const [confirmRelease, setConfirmRelease] = useState(false);

  if (state.error) return <ErrorState message={state.error} onRetry={state.reload} />;
  if (!state.data) {
    return (
      <div className="stack">
        <div className="pxwell skeleton" style={{ height: 220 }} />
        <div className="pxwell skeleton" style={{ height: 300 }} />
      </div>
    );
  }

  const { pokemon, isOwner, evolutions, recentFeeders, originalOwner, owner, lineage } = state.data;
  const bossOrigin = state.data.worldBossOrigin;

  async function patch(body: Record<string, unknown>, message?: string) {
    setBusy(true);
    try {
      const result = await api.patch<{ pokemon: Pokemon }>(`/pokemon/${id}`, body);
      state.setData((current) => (current ? { ...current, pokemon: result.pokemon } : current));
      if (message) push('success', message);
    } catch (caught) {
      push('error', caught instanceof ApiError ? caught.message : 'Could not update.');
    } finally {
      setBusy(false);
      setRenaming(false);
    }
  }

  async function evolveInto(option: EvolutionOption) {
    setBusy(true);
    try {
      const result = await api.post<{
        from: { name: string; sprite: string };
        pokemon: Pokemon;
        unlocked: [];
        badges?: BadgeAward[];
      }>(`/pokemon/${id}/evolve`, { toSpeciesId: option.toSpeciesId });
      setEvolvedTo({ fromName: result.from.name, fromSprite: result.from.sprite, pokemon: result.pokemon });
      celebrate(result.unlocked);
      celebrateBadges(result.badges ?? []);
      state.reload();
      inventory.reload();
      void refresh();
    } catch (caught) {
      push('error', caught instanceof ApiError ? caught.message : 'Could not evolve.');
    } finally {
      setBusy(false);
    }
  }

  async function useItem(itemKey: string) {
    setBusy(true);
    try {
      const result = await api.post<{ message: string }>('/shop/use', { itemKey, pokemonId: id });
      push('reward', result.message, '◆');
      state.reload();
      inventory.reload();
    } catch (caught) {
      push('error', caught instanceof ApiError ? caught.message : 'Could not use that item.');
    } finally {
      setBusy(false);
    }
  }

  async function release() {
    setBusy(true);
    try {
      const result = await api.post<{ reward: number; name: string }>(`/pokemon/${id}/release`);
      push('info', `${result.name} was released. +${result.reward} PokéCoins.`);
      void refresh();
      navigate('/collection');
    } catch (caught) {
      push('error', caught instanceof ApiError ? caught.message : 'Could not release.');
      setConfirmRelease(false);
    } finally {
      setBusy(false);
    }
  }

  const consumables = (inventory.data?.items ?? []).filter(
    (item) => item.category === 'BOOST' && item.key !== 'incubator-boost',
  );

  return (
    <div className="stack">
      <div className="row" style={{ marginBottom: 4 }}>
        <Button size="sm" variant="ghost" onClick={() => navigate(-1)} style={{ color: 'var(--paper)' }}>
          ← Back
        </Button>
      </div>

      <div
        className="grid"
        style={{ gridTemplateColumns: 'minmax(280px, 1fr) minmax(280px, 1.2fr)', alignItems: 'start' }}
      >
        {/* --- Identity ---------------------------------------------------- */}
        <Panel>
          <div className="stack center">
            <div style={{ position: 'relative' }}>
              <img
                className="pixel"
                src={pokemon.sprite}
                alt={pokemon.displayName}
                style={{
                  width: 176,
                  height: 176,
                  objectFit: 'contain',
                  margin: '0 auto',
                  filter: 'drop-shadow(4px 6px 0 rgba(0,0,0,.18))',
                  animation: 'bob 4s ease-in-out infinite',
                }}
              />
            </div>

            <div className="stack-sm">
              <h1 style={{ fontSize: 'var(--text-xl)' }}>
                {pokemon.displayName}
                {genderSuffix(pokemon.displayName, pokemon.gender) && (
                  <span
                    style={{
                      marginLeft: 6,
                      color: pokemon.gender === 'female' ? 'var(--berry)' : 'var(--sky-dark)',
                    }}
                  >
                    {genderSuffix(pokemon.displayName, pokemon.gender)}
                  </span>
                )}
              </h1>
              {pokemon.nickname && (
                <div className="tiny muted">{pokemon.species.displayName}</div>
              )}
              <div className="row row--wrap" style={{ justifyContent: 'center' }}>
                <Link className="chip" to={`/dex/${pokemon.species.id}`}>
                  {dexNumber(pokemon.species.id)}
                </Link>
                {pokemon.species.types.map((type) => (
                  <TypeChip key={type} type={type} />
                ))}
                {pokemon.isShiny && <ShinyChip />}
              </div>
            </div>

            <div style={{ width: '100%' }}>
              <ExpBar pokemon={pokemon} />
            </div>

            {pokemon.species.flavorText && (
              <p className="tiny muted" style={{ fontStyle: 'italic' }}>
                "{pokemon.species.flavorText}"
              </p>
            )}

            {isOwner && (
              <div className="row row--wrap" style={{ justifyContent: 'center' }}>
                <Button
                  size="sm"
                  onClick={() => {
                    setNickname(pokemon.nickname ?? '');
                    setRenaming(true);
                  }}
                >
                  Rename
                </Button>
                <Button
                  size="sm"
                  variant={pokemon.isFavorite ? 'primary' : 'default'}
                  disabled={busy}
                  onClick={() => void patch({ isFavorite: !pokemon.isFavorite })}
                >
                  {pokemon.isFavorite ? '♥ Favourite' : '♡ Favourite'}
                </Button>
                <Button
                  size="sm"
                  disabled={busy}
                  onClick={() => void patch({ isLocked: !pokemon.isLocked }, pokemon.isLocked ? 'Unlocked.' : 'Locked — this Pokémon cannot be released.')}
                >
                  {pokemon.isLocked ? '🔒 Locked' : '🔓 Unlocked'}
                </Button>
              </div>
            )}

            {!isOwner && (
              <div className="tiny muted">
                Owned by <Link to={`/trainer/${owner.username}`}>{owner.username}</Link>
              </div>
            )}
          </div>
        </Panel>

        <div className="stack">
          {/* --- Bond -------------------------------------------------------- */}
          {/* First, above Community, and that ordering is the argument: the
              community numbers are about what other people did, and the bond is
              about what this Pokémon and its trainer are to each other. */}
          {pokemon.bond && (
            <Panel title="♥ Bond" hint={pokemon.bond.name}>
              <div className="stack-sm">
                <BondMeter bond={pokemon.bond} size="lg" showProgress showName={false} />
                <p className="tiny muted" style={{ margin: 0 }}>
                  {pokemon.bond.unlocks}
                </p>
                {pokemon.bond.isSoulbound && (
                  <Link to="/companions" className="btn btn--sm btn--gold">
                    ✦ Certificate {pokemon.bond.certificateNo}
                  </Link>
                )}
                <p className="tiny muted" style={{ margin: 0 }}>
                  Bond grows on its own, from time spent and care taken. It never affects
                  stats, unlocks nothing you could otherwise be shut out of, and cannot be
                  bought.
                </p>
              </div>
            </Panel>
          )}

          {/* --- Ribbons and Memories --------------------------------------- */}
          <PokemonLife pokemonId={id} isOwner={isOwner} />

          {/* --- Community --------------------------------------------------- */}
          <Panel title="♥ Community">
            <div className="stack">
              <div className="row" style={{ gap: 'var(--gap-lg)', flexWrap: 'wrap' }}>
                <Metric label="Times fed" value={formatNumber(pokemon.interactionCount)} />
                <Metric label="Community EXP" value={formatNumber(pokemon.communityExp)} />
                <Metric label="Friendship" value={`${pokemon.friendship}/255`} />
              </div>

              <div className="stack-sm">
                <div className="tiny mono muted">Friendship</div>
                <Bar ratio={pokemon.friendship / 255} />
              </div>

              {recentFeeders.length > 0 ? (
                <div className="stack-sm">
                  <div className="tiny mono muted">Recent helpers</div>
                  {recentFeeders.map((feeder, index) => (
                    <div key={index} className="row row--between tiny">
                      <Link to={`/trainer/${feeder.username}`}>{feeder.username}</Link>
                      <span className="muted">
                        +{formatNumber(feeder.expGranted)} EXP · {timeAgo(feeder.createdAt)}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="tiny muted">
                  {isOwner
                    ? 'Nobody has fed this one yet. Put it on your Team so trainers can find it.'
                    : 'No feeds yet.'}
                </p>
              )}
            </div>
          </Panel>

          {/* --- Evolution --------------------------------------------------- */}
          {isOwner && evolutions.length > 0 && (
            <Panel title="⟳ Evolution">
              <div className="stack-sm">
                {evolutions.map((option) => (
                  <div key={option.toSpeciesId} className="pxwell" style={{ padding: 'var(--gap-sm)' }}>
                    <div className="row" style={{ gap: 'var(--gap-sm)' }}>
                      <img
                        className="pixel"
                        src={option.toSprite}
                        alt=""
                        style={{
                          width: 56,
                          height: 56,
                          filter: option.ready ? 'none' : 'brightness(0) opacity(0.3)',
                        }}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="mono tiny">{option.toSpeciesName}</div>
                        <div className="tiny muted">{option.requirementText}</div>
                        {option.blockedReason && (
                          <div className="tiny" style={{ color: 'var(--berry-dark)' }}>
                            {option.blockedReason}
                          </div>
                        )}
                      </div>
                      <Button
                        size="sm"
                        variant="special"
                        disabled={!option.ready || busy}
                        onClick={() => void evolveInto(option)}
                      >
                        Evolve
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </Panel>
          )}

          {/* --- Items ------------------------------------------------------- */}
          {isOwner && consumables.length > 0 && (
            <Panel title="◆ Use an item">
              <div className="row row--wrap">
                {consumables.map((item) => (
                  <Button key={item.key} size="sm" disabled={busy} onClick={() => void useItem(item.key)}>
                    {item.name} ({item.quantity})
                  </Button>
                ))}
              </div>
            </Panel>
          )}

          {/* --- Stats ------------------------------------------------------- */}
          <Panel title="◈ Details" hint={`IV total ${pokemon.ivTotal}/186`}>
            <div className="stack-sm">
              {(Object.keys(STAT_LABELS) as Array<keyof typeof pokemon.stats>).map((key) => {
                const iv = pokemon.ivs[key];
                return (
                  <div key={key} className="stat-row">
                    <span className="stat-row__name">
                      {STAT_LABELS[key]}
                      {pokemon.nature?.increased === key && (
                        <span style={{ color: 'var(--leaf-dark)' }}> ▲</span>
                      )}
                      {pokemon.nature?.decreased === key && (
                        <span style={{ color: 'var(--berry-dark)' }}> ▼</span>
                      )}
                    </span>
                    <div className="stat-row__track" title={`IV ${iv}/31`}>
                      <div
                        className={`stat-row__fill ${iv >= 26 ? 'stat-row__fill--high' : iv <= 8 ? 'stat-row__fill--low' : ''}`}
                        style={{ width: `${(iv / 31) * 100}%` }}
                      />
                    </div>
                    <span className="stat-row__value">{pokemon.stats[key]}</span>
                  </div>
                );
              })}
            </div>

            <hr className="divider" />

            <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 8 }}>
              <Metric label="Nature" value={pokemon.nature?.name ?? '—'} />
              <Metric label="Height" value={`${(pokemon.species.height / 10).toFixed(1)} m`} />
              <Metric label="Weight" value={`${(pokemon.species.weight / 10).toFixed(1)} kg`} />
              <Metric label="Obtained" value={formatDate(pokemon.obtainedAt)} />
              {originalOwner && <Metric label="Original trainer" value={originalOwner} />}
              <Metric label="Egg groups" value={pokemon.species.eggGroups.map((g) => g.replace('-', ' ')).join(', ') || '—'} />
            </div>

            <hr className="divider" />

            {/* --- Origins --------------------------------------------------- */}
            <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 8 }}>
              <Metric label="Origin" value={lineage.statusLabel} />
              <Metric label="Generation" value={String(lineage.generation)} />
              {lineage.breederName && <Metric label="Bred by" value={lineage.breederName} />}
              {lineage.heritage.tier > 0 && (
                <Metric label="Shiny heritage" value={lineage.heritage.label} />
              )}
            </div>
            <p className="tiny muted" style={{ marginTop: 'var(--gap-sm)' }}>
              {lineage.heritage.tier > 0 ? lineage.heritage.blurb : lineage.statusBlurb}
            </p>
          </Panel>

          {/*
            Where a World Boss Legendary came from — PART 12 and 57.

            Above the family tree on purpose: a Legendary won from a boss has no
            parents, and its provenance *is* its history. Every name here is a
            denormalised string, so it reads correctly after the winner renamed,
            after the event was archived, and in the hands of whoever traded for
            it years later.
          */}
          {bossOrigin && (
            <Panel title="⚡ World Boss origin" className="wb-origin">
              <div className="row" style={{ gap: 'var(--gap)', alignItems: 'flex-start' }}>
                <img
                  className="pixel"
                  src={`/sprites/pokemon/${bossOrigin.bossSpeciesId}.png`}
                  alt=""
                  width={64}
                  height={64}
                />
                <div className="stack-sm" style={{ flex: 1 }}>
                  <strong>
                    {bossOrigin.bossTitle || bossOrigin.eventName} — {bossOrigin.bossName}
                  </strong>
                  <div
                    className="grid"
                    style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 8 }}
                  >
                    <Metric label="Final placement" value={`#${bossOrigin.rank}`} />
                    <Metric label="Original winner" value={bossOrigin.originalWinnerName} />
                    <Metric label="Damage dealt" value={formatNumber(bossOrigin.damage)} />
                    <Metric label="Trainers fighting" value={formatNumber(bossOrigin.participants)} />
                    <Metric label="Defeated" value={formatDate(bossOrigin.defeatedAt)} />
                  </div>
                  <p className="tiny muted" style={{ margin: 0 }}>
                    Won by finishing #{bossOrigin.rank} against {bossOrigin.bossName}. This record
                    travels with the Pokémon and never changes hands.
                  </p>
                </div>
              </div>
            </Panel>
          )}

          {/*
            Its own record against Legendaries — PART 38.

            Rendered only when it has actually fought one, so an ordinary
            Caterpie's page is not a row of zeroes. The counters live on the
            Pokemon itself, so this costs no extra query.
          */}
          {pokemon.worldBoss && (
            <Panel title="World Boss record" raised={false}>
              <div
                className="grid"
                style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 8 }}
              >
                <Metric label="Bosses challenged" value={String(pokemon.worldBoss.bossesFought)} />
                <Metric label="Battles fought" value={String(pokemon.worldBoss.battles)} />
                <Metric label="Total damage" value={formatNumber(pokemon.worldBoss.damage)} />
                <Metric label="Bosses defeated" value={String(pokemon.worldBoss.bossesWon)} />
                {pokemon.worldBoss.bestPlacing != null && (
                  <Metric label="Best placing" value={`#${pokemon.worldBoss.bestPlacing}`} />
                )}
              </div>
            </Panel>
          )}

          {lineage.hasParents && (
            <LineagePanel pokemonId={pokemon.id} rootLabel={pokemon.displayName} />
          )}

          {isOwner && (
            <Panel title="Release" raised={false}>
              <p className="tiny muted">
                Releasing returns a Pokémon to the wild for a small PokéCoin reward. You can never
                release the last one you own of a species.
              </p>
              <Button
                size="sm"
                disabled={pokemon.isLocked || busy}
                onClick={() => setConfirmRelease(true)}
                style={{ marginTop: 'var(--gap-sm)' }}
              >
                {pokemon.isLocked ? 'Locked' : 'Release'}
              </Button>
            </Panel>
          )}
        </div>
      </div>

      {renaming && (
        <Modal onClose={() => setRenaming(false)} labelledBy="rename-title">
          <div className="stack">
            <h2 id="rename-title">Nickname</h2>
            <div className="field">
              <label className="field__label" htmlFor="nick">
                What should we call {pokemon.species.displayName}?
              </label>
              <input
                id="nick"
                className="input"
                value={nickname}
                maxLength={16}
                onChange={(event) => setNickname(event.target.value)}
                placeholder={pokemon.species.displayName}
                autoFocus
              />
            </div>
            <div className="row">
              <Button onClick={() => void patch({ nickname: null }, 'Nickname cleared.')} disabled={busy}>
                Clear
              </Button>
              <div className="spacer" />
              <Button onClick={() => setRenaming(false)}>Cancel</Button>
              <Button variant="go" disabled={busy} onClick={() => void patch({ nickname }, 'Nickname saved.')}>
                Save
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {confirmRelease && (
        <Modal onClose={() => setConfirmRelease(false)} labelledBy="release-title">
          <div className="stack center">
            <img className="pixel" src={pokemon.sprite} alt="" style={{ width: 96, height: 96, margin: '0 auto' }} />
            <h2 id="release-title">Release {pokemon.displayName}?</h2>
            <p className="tiny muted">
              This cannot be undone. All {formatNumber(pokemon.communityExp)} community EXP and{' '}
              {formatNumber(pokemon.interactionCount)}{' '}
              {pokemon.interactionCount === 1 ? 'feed' : 'feeds'} it received will be lost.
            </p>
            <div className="row" style={{ justifyContent: 'center' }}>
              <Button onClick={() => setConfirmRelease(false)}>Keep it</Button>
              <Button variant="primary" loading={busy} onClick={() => void release()}>
                Release
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {evolvedTo && (
        <Modal onClose={() => setEvolvedTo(null)} labelledBy="evolve-title">
          <div className="center stack" style={{ position: 'relative' }}>
            <Confetti count={20} />
            <div className="row" style={{ justifyContent: 'center', gap: 'var(--gap)' }}>
              <img
                className="pixel"
                src={evolvedTo.fromSprite}
                alt=""
                style={{ width: 84, height: 84, filter: 'grayscale(1) opacity(.45)' }}
              />
              <span className="mono" style={{ fontSize: '1.5rem', alignSelf: 'center' }}>
                →
              </span>
              <img
                className="hatch-sprite pixel"
                src={evolvedTo.pokemon.sprite}
                alt=""
                style={{ width: 132, height: 132 }}
              />
            </div>
            <div className="mono tiny" style={{ letterSpacing: '0.18em', color: 'var(--plum-dark)' }}>
              EVOLUTION COMPLETE
            </div>
            <h2 id="evolve-title">
              {evolvedTo.fromName} evolved into {evolvedTo.pokemon.species.displayName}!
            </h2>
            <Button variant="special" size="lg" onClick={() => setEvolvedTo(null)}>
              Wonderful!
            </Button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="tiny muted mono" style={{ fontSize: '0.5625rem', letterSpacing: '0.08em' }}>
        {label.toUpperCase()}
      </div>
      <div className="mono tiny">{value}</div>
    </div>
  );
}

/**
 * A Pokémon's own life: its sash and its scrapbook.
 *
 * Loaded separately from the detail payload rather than folded into it. The
 * timeline is paginated and grows without bound over the years a Companion is
 * meant to last, and the ribbon catalog is the whole catalog — putting either
 * on the main response would make every collection card pay for a page most
 * visits never scroll to.
 */
function PokemonLife({ pokemonId, isOwner }: { pokemonId: string; isOwner: boolean }) {
  const [tab, setTab] = useState<'memories' | 'ribbons'>('memories');
  const [showAll, setShowAll] = useState(false);

  const ribbons = useAsync<{ earned: RibbonData[]; catalog: RibbonData[]; categories: RibbonCategory[] }>(
    () => api.get(`/pokemon/${pokemonId}/ribbons`),
    [pokemonId],
  );
  // `major=true` by default: the Memories page is meant to read like a scrapbook
  // rather than a log, and a list containing "fed by Nadia" four hundred times
  // is a log however nicely it is styled.
  const timeline = useAsync<{ events: BondEvent[]; nextCursor: string | null }>(
    () => api.get(`/pokemon/${pokemonId}/timeline`, { major: showAll ? 'false' : 'true', limit: 40 }),
    [pokemonId, showAll],
  );

  const earned = ribbons.data?.earned ?? [];
  const catalog = ribbons.data?.catalog ?? [];
  const events = timeline.data?.events ?? [];

  return (
    <Panel
      title="✦ Its life"
      action={
        <Tabs
          value={tab}
          onChange={setTab}
          tabs={[
            { value: 'memories', label: 'Memories' },
            { value: 'ribbons', label: 'Ribbons', badge: earned.length || undefined },
          ]}
        />
      }
    >
      {tab === 'memories' ? (
        <div className="stack-sm">
          {events.length === 0 ? (
            <p className="tiny muted" style={{ margin: 0 }}>
              {isOwner
                ? 'Nothing written down yet. Memories start appearing as things happen to it.'
                : 'No memories to show.'}
            </p>
          ) : (
            <ol className="memories">
              {events.map((event) => (
                <li key={event.id} className={`memory ${event.isMajor ? 'memory--major' : ''}`}>
                  <span className="memory__dot" aria-hidden />
                  <div className="memory__body">
                    <strong>{event.title}</strong>
                    {event.body && <p className="tiny muted">{event.body}</p>}
                    <span className="tiny muted mono">{formatDate(event.createdAt)}</span>
                  </div>
                </li>
              ))}
            </ol>
          )}
          <button type="button" className="linkbtn tiny" onClick={() => setShowAll((value) => !value)}>
            {showAll ? 'Just the big moments' : 'Show everything'}
          </button>
        </div>
      ) : (
        <div className="stack-sm">
          <RibbonSash ribbons={earned} emptyLabel="No ribbons yet — they come with time." />
          {catalog.length > 0 && (
            <details className="ribbon-catalog">
              <summary className="tiny muted">
                {catalog.filter((row) => !row.owned).length} still out there
              </summary>
              <RibbonSash
                ribbons={catalog.filter((row) => !row.owned)}
                size={36}
                emptyLabel="Every ribbon in the catalog. Extraordinary."
              />
            </details>
          )}
        </div>
      )}
    </Panel>
  );
}
